// Adaptive input gain for the realtime mic path.
//
// Measured 2026-09-14: this capture path can deliver normal speech ~40 dB below full
// scale (peaks of 100-400 out of 32767). OpenAI's VAD tolerates that and transcribes
// fine; xAI's server_vad ignores it outright — no speech_started, no reply — and
// lowering xAI's threshold does NOT help (verified against the real captured audio).
// The same audio amplified triggers xAI reliably, so the deficiency is level, not
// configuration, and it belongs here rather than in one provider's consumer.
//
// Boost-only by design: a healthy mic is left untouched (gain never goes below 1), and
// the gain does not ramp up during silence — that would just amplify the noise floor
// until VAD fires on nothing.

export const TARGET_PEAK = 0.28;   // ~9200/32767; comfortably clears xAI's VAD
export const MAX_GAIN = 30;        // caps how hard a very quiet mic is pushed
export const NOISE_FLOOR = 0.002;  // below this a frame is room noise, not speech
export const DECAY = 0.95;         // envelope release per frame
export const SMOOTHING = 0.2;      // gain slew per frame, avoids audible pumping

/** Envelope-following gain. Pure: takes and returns the whole AGC state. */
export function nextGain(framePeak, envelope, gain) {
  const env = Math.max(framePeak, envelope * DECAY);
  let target = gain;
  if (env > NOISE_FLOOR) {
    target = Math.min(MAX_GAIN, Math.max(1, TARGET_PEAK / env));
  }
  return { envelope: env, gain: gain + (target - gain) * SMOOTHING };
}

/** Peak absolute sample of a Float32 frame. */
export function framePeakOf(samples) {
  let peak = 0;
  for (let i = 0; i < samples.length; i++) {
    const v = samples[i] < 0 ? -samples[i] : samples[i];
    if (v > peak) peak = v;
  }
  return peak;
}

// While the customer's voice is playing, and for a moment after (speaker → room → mic
// latency), whatever the echo canceller leaves of it is in the mic. Boosting that up to
// TARGET_PEAK turned a faint residual "네" into speech: on 2026-10-22 an iPhone on speaker
// looped "네" with the customer for 26 lines and spent a graded attempt in seconds.
export const PLAYBACK_TAIL_MS = 300;

/**
 * The gain for one mic frame, and the AGC state to keep. During playback (and the tail) the
 * frame goes out at 1× and the state is left as it was, so echo neither gets boosted nor winds
 * the gain; afterwards the boost resumes where it stopped. A trainee talking over the customer
 * is sent at their natural level.
 */
export function micGain(framePeak, agc, msSinceAiAudio) {
  if (msSinceAiAudio < PLAYBACK_TAIL_MS) return { agc, gain: 1 };
  const next = nextGain(framePeak, agc.envelope, agc.gain);
  return { agc: next, gain: next.gain };
}
